import { useEffect, useState } from "react";
import ElPasoHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in El Paso?", a: "We offer El Paso-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The exact timeline depends on your application type, document completeness, and current State Department processing volumes. We review your documents before submission to catch errors that cause delays." },
  { q: "Do I need to visit an office in El Paso?", a: "No. We do not have a physical office in El Paso. Our service is remote: you complete forms online, visit a local acceptance facility (like a El Paso post office or county clerk) for in-person verification, then ship documents to us via overnight courier. We handle the expedited submission at the State Department and return your passport to your El Paso address." },
  { q: "Where is the nearest passport acceptance facility in El Paso?", a: "El Paso has dozens of acceptance facilities including post offices (Downtown, West Side, East Side, Northeast), El Paso County Clerk offices, and select libraries. We provide a curated list of El Paso-area facilities with address, phone, and photo availability. First-time applicants and minors must visit in person; renewals typically ship directly." },
  { q: "Can I get a same-day passport in El Paso?", a: "The El Paso Passport Agency does not accept walk-ins without proof of imminent travel within 14 days and an appointment. Same-day processing is extremely rare and requires exceptional circumstances. For faster-than-14-day needs, our registered courier service offers the fastest legally available channel — as quick as 24 hours with proper documentation." },
  { q: "How much does expedited passport service cost in El Paso?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee that covers document review, overnight shipping, and expedited submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. We offer a best price guarantee." },
  { q: "What documents do I need for an El Paso passport application?", a: "For a new adult passport: proof of citizenship (certified birth certificate or naturalization certificate), government-issued photo ID, one passport photo, and the completed DS-11 form. For renewals: your most recent passport, one photo, and the DS-82 form. For minors: both parents must appear (or provide consent), plus the child's birth certificate and photos. We provide a detailed checklist for El Paso clients." },
  { q: "Can I get a passport photo in El Paso?", a: "Yes. Many El Paso post offices, CVS, Walgreens, and Walmart locations offer passport photos. Some post offices include photo services with their acceptance facility hours. We recommend confirming photo availability before visiting. Passport photos must meet strict State Department specifications — 2x2 inches, white background, no glasses, neutral expression." },
  { q: "Do I need an appointment at El Paso passport acceptance facilities?", a: "Many El Paso post offices require appointments for passport acceptance. Some accept walk-ins during limited hours. We recommend calling ahead. El Paso County Clerk offices and larger post offices typically offer more flexible appointment times. We can help you find the facility with the next available appointment." },
  { q: "Can I track my El Paso passport application?", a: "Yes. Once we submit your application, we provide tracking information. You can also check the State Department's online passport status system. Our courier service includes tracking at every stage: from El Paso to our processing center, through State Department review, and return shipping to your Texas address." },
  { q: "What if my passport is lost or stolen in El Paso?", a: "File a police report if stolen, then complete Form DS-64 (Statement Regarding Lost or Stolen Passport) along with Form DS-11 for a replacement. You will need to visit an acceptance facility in person and provide citizenship documentation again. If you have upcoming travel, our expedited service can process the replacement as fast as 24 hours." },
];

function ElPasoStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) {
            setActive(id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" },
    { id: "services", label: "Services" },
    { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" },
    { id: "destinations", label: "Destinations" },
    { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" },
    { id: "comparison", label: "Compare" },
    { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" },
    { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (
          <a
            key={l.id}
            href={`#${l.id}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${
              active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"
            }`}
          >
            {l.label}
          </a>
        ))}
      </div>
    </div>
  );
}

function ElPasoProcessingTimeSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">El Paso Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">We offer multiple speed tiers for El Paso-area travelers. Choose the timeline that matches your departure urgency.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For El Paso travelers with imminent departure within 3 days. Includes same-day document review and overnight courier submission." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For business travelers and families with travel in 1–2 weeks. Balanced speed and value with full document review." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For El Paso residents planning ahead. Still faster than standard post office processing (10–13 weeks) with professional document handling." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ElPasoServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for El Paso travelers with imminent departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for El Paso residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time application guidance for El Paso adults with acceptance facility support." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 form guidance for El Paso residents." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for El Paso families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-stack-line", title: "Second Passport", desc: "Second passport issuance for frequent El Paso business travelers." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors that delay El Paso applications." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">El Paso Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Comprehensive passport solutions for El Paso's diverse travel community — from military families to maquiladora workers to cross-border commuters.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ElPasoLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for El Paso</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            El Paso is Texas's westernmost major city — a unique border metropolis where U.S. and Mexican cultures blend seamlessly across the Rio Grande. With nearly 700,000 residents and a metropolitan area approaching 1 million, El Paso is the sixth-largest city in Texas and one of the most important border crossings in the United States. From the military community at Fort Bliss to the maquiladora workforce commuting to Ciudad Juarez, from the historic Chihuahuita neighborhood to the modern developments of the Upper Valley, El Paso generates passport demand with a character entirely its own.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Fort Bliss is one of the largest military installations in the United States, home to over 30,000 active-duty personnel and supporting thousands of military families. Army deployment cycles, overseas training assignments, and PCS moves to bases in Germany, Japan, Korea, and Italy create constant passport demand. Military families often need expedited service when orders arrive with short notice, or when a child's passport expires before a family relocation. We understand the urgency of military timelines and the specific documentation requirements for dependent family members.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            El Paso's economy is deeply tied to cross-border trade. Thousands of workers commute daily to maquiladora plants in Ciudad Juarez, and many El Paso businesses operate on both sides of the border. While a passport card suffices for land crossings, international business travel to Mexico City, Monterrey, or Guadalajara requires a full passport book. Many El Paso residents also need visas for extended work assignments in Mexico. The proximity to the border creates a unique pattern where residents may not have needed a full passport for years, but suddenly require one for air travel or business beyond the border zone.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The city's demographics reflect its binational identity. El Paso has one of the highest percentages of Hispanic residents of any major U.S. city — over 80% of the population. Family heritage travel to Mexico is a deeply rooted tradition, with multi-generational trips to Chihuahua, Durango, Zacatecas, and Jalisco happening throughout the year. Grandparents, parents, and children all need current passports, and the renewal cycle often catches families by surprise when the whole group is planning a trip together.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            El Paso International Airport (ELP) offers direct flights to major U.S. hubs and growing international connectivity. The airport serves the entire West Texas and Southern New Mexico region, including Las Cruces, Alamogordo, and Carlsbad. For El Paso residents, ELP is the primary gateway to international travel — and the place where passport issues are most often discovered. The nearest passport agency is in Houston, over 700 miles away, making remote expedited service the most practical option for urgent El Paso travelers.
          </p>
        </div>
      </div>
    </section>
  );
}

function ElPasoTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for El Paso Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">ELP — El Paso International Airport</h3>
                <p className="text-gray-400 text-xs">Serving West Texas and Southern New Mexico</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              El Paso International Airport offers direct flights to Dallas, Houston, Phoenix, Denver, and Los Angeles, with connections to virtually any international destination. The airport handles over 3 million passengers annually and is the primary gateway for the entire West Texas region. For El Paso residents, ELP is the starting point for military deployments, family heritage trips, and business travel. The nearest passport agency is in Houston — over 700 miles away — making remote expedited service the most practical option for urgent travelers.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> With no passport agency within 700 miles, El Paso residents should plan ahead or use a registered courier service. Last-minute attempts to drive to Houston or Phoenix are not practical for same-day processing.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-shield-star-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Fort Bliss &amp; Military Deployment</h3>
                <p className="text-gray-400 text-xs">One of the largest U.S. Army installations</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Fort Bliss hosts over 30,000 active-duty personnel and supports thousands of military families. Deployment cycles, PCS moves, and overseas training assignments create constant passport demand. Military families often receive orders with 2–4 weeks notice, and dependent children need current passports for international relocations. The El Paso military community is one of our most time-sensitive client groups, and we have developed specialized workflows for processing military family passport applications.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Military dependents may need passports for both official travel and personal travel. Check if your orders include SOFA stamp requirements — some countries require specific passport documentation for military families.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-building-2-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Maquiladora &amp; Cross-Border Business</h3>
                <p className="text-gray-400 text-xs">El Paso-Juarez manufacturing corridor</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The El Paso-Juarez manufacturing corridor is one of the world's largest cross-border production zones. Thousands of El Paso workers and managers cross daily for maquiladora operations. While a passport card works for land crossings, air travel to Mexico City, Monterrey, or other Mexican business hubs requires a full passport book. Many El Paso professionals also need visas for extended work assignments. Business travel often has immovable meeting dates that require reliable passport processing.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> If you regularly cross the border for work, consider getting both a passport card (for daily land crossings) and a passport book (for air travel). The card is more convenient for routine crossings.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-home-heart-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Family Heritage Travel</h3>
                <p className="text-gray-400 text-xs">Multi-generational trips to Mexican home states</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              El Paso's deep Mexican heritage means family travel to ancestral home states is a year-round tradition. Trips to Chihuahua, Durango, Zacatecas, Jalisco, and Michoacan often involve extended family groups — grandparents, parents, children, and cousins. These multi-generational trips require every participant to have a current passport. A single expired passport can derail the entire family itinerary. We regularly process group applications for El Paso families planning heritage travel.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> For family group travel, check everyone's passport expiration at least 6 months before your trip. Children's passports expire in 5 years, so they often need renewal before adult family members.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-road-map-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">El Paso Isolation — No Nearby Passport Agency</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              The nearest regional passport agency to El Paso is in Houston, over 700 miles away. The Phoenix agency is over 400 miles. For El Paso residents, driving to a passport agency is not a practical option for urgent processing. Our registered courier service bridges this geographic gap, offering El Paso the same expedited processing that Houston and Dallas residents access locally.
            </p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function ElPasoDestinationsSection() {
  const destinations = [
    { name: "Mexico", context: "Family heritage trips to Chihuahua, Durango, Zacatecas; business travel to Mexico City, Monterrey, Guadalajara", urgency: "Heritage travel peaks during Christmas, Easter, and summer vacation months", icon: "ri-map-pin-2-line", flag: "MX" },
    { name: "Germany & Europe", context: "Fort Bliss military deployments and PCS moves to bases in Germany, Italy, and Belgium", urgency: "Military orders often arrive with 2–4 weeks notice for overseas relocation", icon: "ri-map-pin-2-line", flag: "DE" },
    { name: "South Korea & Japan", context: "Military assignments to Camp Humphreys, Yokota, and other Pacific bases", urgency: "PCS orders have fixed report dates that cannot be delayed for passport issues", icon: "ri-map-pin-2-line", flag: "KR" },
    { name: "Cancun & Riviera Maya", context: "Resort vacations, spring break, and honeymoon travel from El Paso families", urgency: "Vacation bookings often happen 3–6 weeks out with online deals", icon: "ri-map-pin-2-line", flag: "BS" },
    { name: "Spain & Portugal", context: "Heritage travel for families with Spanish and Portuguese roots; European tourism", urgency: "Summer travel to Europe is planned 2–3 months ahead but passports are checked late", icon: "ri-map-pin-2-line", flag: "ES" },
    { name: "Colombia & South America", context: "Growing business connections and family heritage travel to South America", urgency: "Business meetings and family events have fixed dates requiring reliable processing", icon: "ri-map-pin-2-line", flag: "CO" },
  ];
  const commonSituations = [
    { stat: "38%", label: "of El Paso expedited requests are military-related with deployment or PCS deadlines" },
    { stat: "4–8 Weeks", label: "average notice El Paso travelers have before international departure or military move" },
    { stat: "Dec–Jan", label: "peak season when Christmas heritage travel, military winter rotations, and snowbird departures overlap" },
    { stat: "36 Hours", label: "shortest turnaround we have accommodated for a Fort Bliss family with PCS orders to Germany" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where El Paso Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/houston" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Houston <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/dallas" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Dallas <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/austin" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Austin <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ElPasoTestimonialsSection() {
  const testimonials = [
    { name: "Sgt. Michael R.", location: "Fort Bliss", text: "Received PCS orders to Germany with 3 weeks notice. My wife and two kids needed passports. Fast Passport Center had all three processed and back in 8 days. Saved our move timeline.", rating: 5 },
    { name: "Maria G.", location: "West Side", text: "Our family trip to Chihuahua for Christmas was in 2 weeks and my daughter's passport had expired. They walked me through the process and got it back in 5 days. Our Christmas was saved.", rating: 5 },
    { name: "Carlos T.", location: "East Side", text: "I commute to Juarez for work but needed to fly to Mexico City for a meeting. My passport was expired. They processed it in 4 days and I made my meeting. Professional service.", rating: 5 },
    { name: "Jennifer L.", location: "Horizon City", text: "Booked a Cancun vacation and realized my husband's passport had water damage. They handled the replacement and got it back with a week to spare. Worth every penny.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What El Paso Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>
                ))}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ElPasoAcceptanceFacilitiesSection() {
  const facilities = [
    { name: "El Paso Main Post Office", address: "8401 Montana Ave, El Paso, TX 79925", phone: "(915) 581-4241", photo: "Yes", appointment: "Required" },
    { name: "Downtown El Paso Post Office", address: "401 E Main St, El Paso, TX 79901", phone: "(915) 541-7621", photo: "Yes", appointment: "Walk-in" },
    { name: "El Paso County Clerk", address: "500 E San Antonio Ave, El Paso, TX 79901", phone: "(915) 546-2071", photo: "No", appointment: "Required" },
    { name: "Westside Station Post Office", address: "7845 N Mesa St, El Paso, TX 79932", phone: "(915) 585-8521", photo: "Yes", appointment: "Required" },
    { name: "Northeast Post Office", address: "10420 Montwood Dr, El Paso, TX 79935", phone: "(915) 591-7801", photo: "Yes", appointment: "Required" },
    { name: "Horizon City Post Office", address: "14999 Darrington Rd, Horizon City, TX 79928", phone: "(915) 852-2154", photo: "Yes", appointment: "Required" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">El Paso Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility in person. Here are El Paso-area options with hours and contact information.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`${f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}`}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. We recommend calling ahead to confirm availability. We can also help you find the facility with the earliest appointment slot.</p>
        </div>
      </div>
    </section>
  );
}

function ElPasoComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">El Paso Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Third-Party Resellers</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "7–14 days" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "Minimal or extra fee" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Often 30–100% markup" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Reseller, not registered" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "Often limited" },
                { feature: "Shipping Included", us: "Overnight both ways", post: "You pay shipping", reseller: "Varies by plan" },
                { feature: "El Paso-Specific Guidance", us: "Yes, local facility knowledge", post: "Generic", reseller: "Generic" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "Varies" },
                { feature: "Same-Day Submission", us: "Yes, for emergency tier", post: "No", reseller: "Rarely" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function ElPasoMistakesSection() {
  const mistakes = [
    { title: "Waiting Until the Last Minute", desc: "El Paso's military community often receives orders with short notice. Waiting until the last minute leaves no buffer for document errors or appointment delays." },
    { title: "Confusing Passport Card vs. Book", desc: "Many El Paso residents use a passport card for daily border crossings. But air travel to Mexico City, Europe, or Asia requires a passport book. Double-check which document your trip requires." },
    { title: "Unacceptable Passport Photos", desc: "Many El Paso CVS and Walgreens photo technicians are not trained on current State Department photo standards. A rejected photo adds 1–2 weeks to your processing time." },
    { title: "Forgetting Parental Consent", desc: "For minors in El Paso, both parents must appear or provide notarized consent. Single-parent applications missing the DS-3053 form are rejected outright." },
    { title: "Not Checking Visa Requirements", desc: "El Paso military families deploying to Germany, Korea, or Japan need to verify if their destination requires visas in addition to passports. Some countries have specific requirements for military dependents." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common El Paso Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ElPasoHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our El Paso-specific intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit an El Paso-area acceptance facility. We provide the nearest location with available appointments." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed application package via overnight courier to our processing center. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit your application through our registered courier channel with the State Department. You receive tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Your passport is delivered to your El Paso address via secure overnight shipping. Most El Paso clients receive it within their chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for El Paso Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ElPasoFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">El Paso Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ElPasoHelpfulResourcesSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for El Paso Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <a href="https://travel.state.gov/content/travel/en/passports.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-government-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">U.S. State Department</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Official passport information, forms, and processing status updates.</p>
          </a>
          <a href="https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-earth-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Travel Advisories</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Current safety and entry requirements for every country.</p>
          </a>
          <a href="https://www.cbp.gov/travel/travel-industry-persons/visa-waiver-program" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-visa-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Visa Waiver Program</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Check if your destination participates in the ESTA visa waiver program.</p>
          </a>
        </div>
      </div>
    </section>
  );
}

function ElPasoFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your El Paso Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">
          Whether you are deploying from Fort Bliss in 3 days or flying to Mexico City in 3 weeks, we will get your passport processed on time. No surprises. No delays. Just fast, reliable service.
        </p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function ElPasoPage() {
  useEffect(() => {
    document.title = "Emergency Passport El Paso, TX — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/el-paso/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in El Paso, TX — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Fort Bliss military families, maquiladora workers, and cross-border commuters.");
    setMeta("geo.placename", "El Paso, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "31.7619;-106.4850");
    setMeta("ICBM", "31.7619, -106.4850");
    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for El Paso travelers with immediate departure needs." },
      { name: "Passport Renewal", description: "Fast passport renewal for El Paso residents with recent passport issuance." },
      { name: "New Passport", description: "First-time application assistance for El Paso adults and students." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for El Paso residents." },
      { name: "Child Passport", description: "Passport services for minors under 16 in El Paso." },
      { name: "Name Change", description: "Passport correction after marriage, divorce, or legal name changes." },
      { name: "Second Passport", description: "Second passport issuance for frequent El Paso business travelers." },
    ];
    const graph = [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
        { "@type": "ListItem", position: 2, name: "Texas", item: "https://fastpassportcenter.com/texas/" },
        { "@type": "ListItem", position: 3, name: "El Paso Passport Services", item: pageUrl },
      ]},
      { "@type": "LocalBusiness", name: "Fast Passport Center", description: "Fast Passport Center does not have a physical office in El Paso — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for El Paso and West Texas travelers.", url: pageUrl, telephone: "+1-877-253-0084", areaServed: { "@type": "City", name: "El Paso", containedInPlace: { "@type": "State", name: "Texas" } }, priceRange: "$$$", openingHours: ["Mo-Fr 09:00-18:00"], aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" }},
      ...servicesList.map((s) => ({ "@type": "Service", serviceType: s.name, provider: { "@type": "LocalBusiness", name: "Fast Passport Center" }, areaServed: { "@type": "City", name: "El Paso" }, description: s.description })),
      { "@type": "FAQPage", mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <ElPasoHeroSection />
      <ElPasoStickyNav />
      <ElPasoProcessingTimeSection />
      <ElPasoServicesSection />
      <ElPasoLocalAreaSection />
      <ElPasoTravelTimingSection />
      <ElPasoDestinationsSection />
      <ElPasoTestimonialsSection />
      <ElPasoAcceptanceFacilitiesSection />
      <ElPasoComparisonSection />
      <ElPasoMistakesSection />
      <ElPasoHowItWorksSection />
      <ElPasoFAQSection />
      <ElPasoHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/el-paso" state="TX" />
      <ElPasoFinalCTA />
    </div>
  );
}